import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { PropsOf } from '@emotion/react';
import type { ForwardedRef, ReactNode } from 'react';
import * as tokens from '@tokens';
import { forwardRef, useImperativeHandle, useRef, useContext } from 'react';
import type { ButtonImperativeHandleRef } from '@components/DropdownMenu/dropdown-menu-models';
import type {
  ButtonOrLinkElement,
  ButtonOrLinkProps,
} from '@common/ButtonOrLink';
import { externalRel } from '@common/ButtonOrLink';
import { HotsauceContext } from '@providers/hotsauceContext';
import { navigationClickHandler } from '@utils/navigation-handler';
import { shouldForwardProp } from '@utils/styledProps';
import { Divider } from '@components/Divider/Divider';

export type DropdownBaseItemProps<
  T extends ButtonOrLinkElement = ButtonOrLinkElement,
> = Omit<ButtonOrLinkProps<T>, 'ref'> & {
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Should the item be selected.
   */
  isSelected?: boolean;
  /**
   * Should the item be hidden.
   */
  isHidden?: boolean;
  /**
   * DropdownBaseItem content.
   */
  children: ReactNode;
  /**
   * Kind of Dropdown item used.
   */
  kind?: 'ghost' | 'danger_ghost';
  /**
   * Has a divider above the item
   */
  hasDivider?: boolean;
};

const StyledItem = styled('li')`
  padding: 0;
`;

const baseStyles = (isSelected) => css`
  ${tokens.text('body2')};
  display: inline-flex;
  align-items: center;
  padding: 0 ${tokens.spacer.x2};
  width: 100%;
  height: ${tokens.spacer.x5};
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  color: ${tokens.color.text_default};

  &:hover {
    background: ${tokens.color.bg_hover};
  }

  &:focus {
    background: ${tokens.color.ui_background};
    box-shadow: 0 0 0 1px ${tokens.color.ui_background},
      0 0 0 3px ${tokens.color.focus};
    transform: scale(1);
    z-index: 2;
  }

  ${isSelected &&
  `
    background-color: ${tokens.color.bg_active};
  `}
`;

const ghostStyles = css`
  color: ${tokens.color.text_default};
`;

const dangerGhostStyles = css`
  color: ${tokens.color.danger_cta};
  &:hover,
  &:focus {
    color: ${tokens.color.danger_cta};
  }
`;

const DROPDOWN_BUTTON_ITEM_KIND_STYLES = {
  ghost: ghostStyles,
  danger_ghost: dangerGhostStyles,
};

const StyledButton = styled('button', {
  shouldForwardProp: shouldForwardProp(['isSelected', 'kind']),
})<Omit<DropdownBaseItemProps<'button'>, 'as' | 'children'>>`
  ${({ isSelected }) => baseStyles(isSelected)};

  ${({ kind }) => DROPDOWN_BUTTON_ITEM_KIND_STYLES[kind]};

  &[disabled] {
    background-color: transparent;
    color: ${tokens.color.text_disabled};
    cursor: not-allowed;
  }
`;

const StyledLink = styled('a', {
  shouldForwardProp: shouldForwardProp(['isSelected', 'isExternal']),
})<Omit<DropdownBaseItemProps<'a'>, 'as' | 'children'>>`
  text-decoration: none;
  ${({ isExternal }) =>
    isExternal &&
    `
    span:last-child {
      visibility: hidden;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      span:last-child {
        visibility: visible;
      }
    }
  `}

  &:hover,
  &:focus {
    text-decoration: none;
    color: inherit;
  }

  ${({ isSelected }) => baseStyles(isSelected)};

  ${({ kind }) => DROPDOWN_BUTTON_ITEM_KIND_STYLES[kind]};
`;

type PassedDropdownBaseItemProps<T extends ButtonOrLinkElement> = Omit<
  DropdownBaseItemProps<T>,
  'as' | 'children'
>;

const DropdownBaseItemRender = <
  T extends ButtonOrLinkElement = ButtonOrLinkElement,
>(
  { as, children, isHidden, hasDivider, ...props }: DropdownBaseItemProps<T>,
  ref: ForwardedRef<ButtonImperativeHandleRef>,
): JSX.Element | null => {
  const { onNavigate } = useContext(HotsauceContext) ?? {};

  const itemRef = useRef<HTMLButtonElement | HTMLAnchorElement>();

  const focusItem = () => {
    if (itemRef.current) {
      itemRef.current.focus();
    }
  };

  useImperativeHandle<unknown, ButtonImperativeHandleRef>(
    ref,
    () => {
      return { focus: focusItem };
    },
    [focusItem],
  );

  if (isHidden) {
    return null;
  }

  // link case
  if (as === 'a') {
    const { isExternal, rel, ...linkProps } =
      props as PassedDropdownBaseItemProps<'a'>;

    const onClick = (event) =>
      navigationClickHandler({
        event,
        isExternal,
        linkProps: linkProps as PropsOf<'a'>,
        onNavigate,
      });

    return (
      <StyledItem>
        {hasDivider && <Divider spacing="xs" orientation="horizontal" />}
        <StyledLink
          {...linkProps}
          ref={itemRef as ForwardedRef<HTMLAnchorElement>}
          rel={isExternal ? externalRel(rel) : undefined}
          isExternal={isExternal}
          target={isExternal ? '_blank' : undefined}
          onClick={onClick}
          role="menuitem"
        >
          {children}
        </StyledLink>
      </StyledItem>
    );
  }

  // button case
  const { isDisabled, kind, ...buttonProps } =
    props as PassedDropdownBaseItemProps<'button'>;

  return (
    <StyledItem>
      {hasDivider && <Divider spacing="xs" orientation="horizontal" />}
      <StyledButton
        {...buttonProps}
        ref={itemRef as ForwardedRef<HTMLButtonElement>}
        disabled={isDisabled}
        role="menuitem"
        type="button"
        kind={kind || 'ghost'}
      >
        {children}
      </StyledButton>
    </StyledItem>
  );
};

/**
 * DropdownBaseItem component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Provides a low-level implementation for the DropdownItem component.
 * @see DropdownItem
 */
export const DropdownBaseItem = forwardRef(DropdownBaseItemRender);

DropdownBaseItem.defaultProps = {
  as: 'button',
};

DropdownBaseItem.displayName = 'DropdownBaseItem';
